import { forwardRef, MutableRefObject, useCallback, useRef, useState } from 'react'

import { useGSAP } from '@/composables/useGSAP'
// import AudioEngine from "@/webgl/audio/AudioEngine";

export type ToggleButtonProps = React.HTMLProps<HTMLLabelElement> & {
  label?: string
  labelPosition?: 'top' | 'bottom' | 'left' | 'right'
  labelTheme?: 'default' | 'pill'
  labelClassName?: string
  // icon: UIIconType;
  // iconChecked: UIIconType;
  theme?: 'text-peach' | 'text-scarlet'
  checked: boolean
  setChecked?: (checked: boolean) => void
}

const ToggleButton = forwardRef<HTMLLabelElement, ToggleButtonProps>(
  (
    {
      className,
      children,
      label,
      labelPosition = 'bottom',
      labelTheme = 'default',
      labelClassName,
      // icon,
      // iconChecked,
      theme = 'text-peach',
      disabled,
      checked,
      setChecked,
    },
    buttonRef
  ) => {
    const hoverBackgroundRef = useRef<HTMLDivElement>(null)
    const [hovering, setHovering] = useState(false)

    const { gsap } = useGSAP()

    const [timeline, setTimeline] = useState<gsap.core.Timeline | null>(null)

    const getZoom = (x: number, y: number, width: number, height: number) => {
      const w = x > width / 2 ? x : width - x
      const h = y > height / 2 ? y : height - y
      const dist = Math.sqrt(w * w + h * h)
      const factor = 1
      const zoom = (dist / 40) * factor

      return zoom
    }

    const onEnter = useCallback(
      (event: React.MouseEvent<HTMLLabelElement>) => {
        if (!hovering) {
          // AudioEngine.play(UIAudio.Hover);
          setHovering(true)
          const button = (buttonRef as MutableRefObject<HTMLLabelElement>)?.current
          if (button === null) return

          const rect = button.getBoundingClientRect()
          const buttonPosition = { x: rect.x, y: rect.y }

          const position = { x: event.clientX - buttonPosition.x, y: event.clientY - buttonPosition.y }

          const zoom = getZoom(position.x, position.y, rect.width, rect.height)
          timeline?.kill()
          const tl = gsap.timeline()

          tl.fromTo(
            hoverBackgroundRef.current,
            {
              scale: 0,
              x: position.x,
              y: position.y,
            },
            {
              scale: zoom,
              duration: 0.666,
              ease: 'quart.out',
            }
          )
          setTimeline(tl)
        }
      },
      [buttonRef, timeline, gsap, hoverBackgroundRef, hovering, setHovering]
    )

    const onLeave = useCallback(
      (event: React.MouseEvent<HTMLLabelElement>) => {
        setHovering(false)
        const button = (buttonRef as MutableRefObject<HTMLLabelElement>)?.current
        if (button === null) return

        const rect = button.getBoundingClientRect()
        const buttonPosition = { x: rect.x, y: rect.y }

        const position = {
          x: Math.max(0, Math.min(rect.width, event.clientX - buttonPosition.x)),
          y: Math.max(0, Math.min(rect.height, event.clientY - buttonPosition.y)),
        }
        const zoom = getZoom(position.x, position.y, rect.width, rect.height)
        timeline?.kill()
        const tl = gsap.timeline()
        tl.fromTo(
          hoverBackgroundRef.current,
          {
            scale: zoom,
            x: position.x,
            y: position.y,
          },
          {
            scale: 0,
            duration: 0.333,
            ease: 'quart.out',
          }
        )
        setTimeline(tl)
      },
      [buttonRef, timeline, gsap, hoverBackgroundRef, setHovering]
    )

    const onInputChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setChecked(event.target.checked)
      },
      [setChecked]
    )

    const classNameLabelPosition = (() => {
      switch (labelPosition) {
        case 'top':
          return 'left-1/2 -translate-x-1/2 bottom-full mb-12 group-hover:translate-y-3'
        case 'right':
          return 'left-full ml-12 top-1/2 -translate-y-1/2 group-hover:-translate-x-3'
        case 'left':
          return 'right-full mr-12 top-1/2 -translate-y-1/2 group-hover:-translate-x-3'
        default:
          return 'left-1/2 -translate-x-1/2 top-full mt-12 group-hover:-translate-y-3'
      }
    })()
    const classNameLabelTheme = (() => {
      switch (labelTheme) {
        case 'pill':
          return 'text-body-s h-40 hidden md:flex items-center justify-center bg-dark-cream rounded-8 px-16 py-4'
        default:
          return 'text-18 text-tertiary font-semibold leading-relaxed hidden md:block'
      }
    })()

    const [showParticles, setShowParticles] = useState(false)
    const onMouseDown = useCallback(() => {
      setShowParticles(true)
    }, [setShowParticles])
    const onParticlesCompleted = useCallback(() => {
      setShowParticles(false)
    }, [setShowParticles])
    return (
      <label
        ref={buttonRef}
        className={`ToggleButton cursor-pointer rounded-12 relative aspect-square w-48 flex items-center justify-center group disabled:opacity-80 transition-opacity duration-333 ease-out-quad ${disabled ? 'pointer-events-none' : 'pointer-events-auto'} ${checked ? 'active' : ''} ${className ? className : ''}`}
        onMouseDown={onMouseDown}
      >
        {/* <span className="absolute -top-4 -left-4 -bottom-4 -right-4 border-2 border-scarlet rounded-28" /> */}

        <input
          checked={checked}
          onChange={onInputChange}
          type='checkbox'
          className='peer absolute top-0 left-0 opacity-0'
        />

        <span className={`Button__Background ${theme}`}>
          <svg
            className={'size-48'}
            viewBox='0 0 80 80'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            aria-hidden='true'
          >
            <path
              d='M51.6472 2.00725L39.7494 0.262506C33.2511 -0.69127 26.6401 0.976557 21.3699 4.89807L11.7232 12.077C6.45565 15.9973 2.95975 21.852 2.00725 28.3502L0.262506 40.2481C-0.691271 46.7463 0.976557 53.3574 4.89807 58.6275L12.077 68.2742C15.9985 73.5431 21.852 77.039 28.3502 77.9927L40.2481 79.7375C46.7463 80.6913 53.3574 79.0234 58.6275 75.1019L68.2742 67.923C73.5431 64.0015 77.039 58.148 77.9928 51.6498L79.7375 39.7519C80.6913 33.2537 79.0235 26.6426 75.102 21.3725L67.923 11.7258C64.0015 6.45693 58.1481 2.96103 51.6498 2.00725H51.6472Z'
              fill='currentColor'
            />
          </svg>
        </span>

        {children}
        {!!label && (
          <span
            className={`ToggleButton__Name absolute pointer-events-none transition-transform duration-333 ease-out-quart ${classNameLabelPosition} ${classNameLabelTheme} ${labelClassName}`}
          >
            {label}
          </span>
        )}

        {/* {showParticles && (
        <UIParticles
          className="absolute top-0 left-0 w-full h-full"
          max={4}
          exclude={{ colors: ['peach'], icons: [] }}
          onCompleted={onParticlesCompleted}
          variant="small"
        />
      )} */}
      </label>
    )
  }
)

export default ToggleButton
